"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { laadFirestore } from "@/lib/firestore-client"; // MOW-PERF-OKT26

interface Review {
  id: string;
  displayName: string;
  rating: number;
  comment: string;
  verified: boolean;
  createdAt: any;
}

interface ReviewsSectionProps {
  productId: string;
}

export default function ReviewsSection({ productId }: ReviewsSectionProps) {
  // MOW-PERF-OKT26: de Auth-SDK laadt hier alleen als de browser sporen van een eerdere login heeft.
  const { user } = useAuth("bij-sporen");
  const { showToast } = useToast();
  
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadReviews();
  }, [productId]);

  async function loadReviews() {
    try {
      const { db, collection, query, where, getDocs, orderBy } = await laadFirestore();
      const q = query(
        collection(db, "reviews"),
        where("productId", "==", productId),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      const list: Review[] = [];
      snap.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as Review);
      });
      setReviews(list);
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) {
      showToast("U moet ingelogd zijn om een review te schrijven", "error");
      return;
    }
    if (comment.length < 10) {
      showToast("Uw review moet ten minste 10 tekens lang zijn", "error");
      return;
    }
    if (!displayName.trim()) {
      showToast("Vul a.u.b. uw naam in", "error");
      return;
    }

    setSubmitting(true);
    try {
      const { db, collection, addDoc, serverTimestamp } = await laadFirestore();
      await addDoc(collection(db, "reviews"), {
        productId,
        userId: user.uid,
        displayName: displayName.trim(),
        rating,
        comment: comment.trim(),
        verified: false, // Default false, admin/backend can verify it
        createdAt: serverTimestamp(),
      });
      
      showToast("Review succesvol geplaatst! Deze wordt getoond na verwerking.", "success");
      setComment("");
      setDisplayName("");
      setRating(5);
      
      // Reload reviews
      loadReviews();
    } catch (error: any) {
      console.error("Error submitting review:", error);
      showToast(error.message || "Fout bij plaatsen van review", "error");
    } finally {
      setSubmitting(false);
    }
  }

  const averageRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div style={{ marginTop: "40px" }}>
      <h3 style={{ fontSize: "1.4rem", fontFamily: "var(--font-serif)", marginBottom: "20px", color: "var(--color-secondary)" }}>
        Klantbeoordelingen {averageRating && `(${averageRating}/5)`}
      </h3>

      {loading ? (
        <p style={{ color: "var(--color-gray-400)", fontSize: "0.9rem" }}>Reviews laden...</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "40px" }}>
          {/* Reviews List */}
          <div>
            {reviews.length === 0 ? (
              <p style={{ color: "var(--color-gray-500)", fontStyle: "italic", fontSize: "0.95rem" }}>
                Er zijn nog geen beoordelingen voor dit product. Wees de eerste om een review te schrijven!
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {reviews.map((review) => (
                  <div key={review.id} style={{ borderBottom: "1px solid var(--color-gray-150)", paddingBottom: "15px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "6px" }}>
                      <span style={{ fontWeight: 600, fontSize: "0.95rem", color: "var(--color-secondary)" }}>
                        {review.displayName}
                        {review.verified && (
                          <span style={{ fontSize: "0.75rem", color: "var(--color-success)", marginLeft: "8px", fontWeight: "normal" }}>
                            ✓ Geverifieerde aankoop
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "var(--color-gray-400)" }}>
                        {review.createdAt?.seconds 
                          ? new Date(review.createdAt.seconds * 1000).toLocaleDateString("nl-NL")
                          : "Zojuist"}
                      </span>
                    </div>
                    {/* Stars */}
                    <div style={{ color: "#ffb703", fontSize: "0.95rem", marginBottom: "8px" }}>
                      {"★".repeat(review.rating)}
                      {"☆".repeat(5 - review.rating)}
                    </div>
                    <p style={{ color: "var(--color-gray-700)", fontSize: "0.9rem", lineHeight: "1.5" }}>
                      {review.comment}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Review Form */}
          <div style={{ background: "var(--color-off-white)", padding: "25px", borderRadius: "4px", border: "1px solid var(--color-gray-200)", height: "fit-content" }}>
            <h4 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "15px", color: "var(--color-secondary)" }}>
              Schrijf een beoordeling
            </h4>
            
            {user ? (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                <div>
                  <label id="rating-label" style={{ display: "block", fontSize: "0.8rem", fontWeight: 700, marginBottom: "6px" }}>
                    Waardering
                  </label>
                  <div role="radiogroup" aria-labelledby="rating-label" style={{ display: "flex", gap: "6px", fontSize: "1.5rem", color: "#ffb703" }}>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setRating(num)}
                        aria-label={`${num} van 5 sterren`}
                        aria-pressed={num <= rating}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "inherit" }}
                      >
                        {num <= rating ? "★" : "☆"}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="displayName" style={{ display: "block", fontSize: "0.8rem", fontWeight: 700, marginBottom: "6px" }}>
                    Uw naam
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    required
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    style={{ width: "100%", padding: "10px", border: "1px solid var(--color-gray-300)", borderRadius: "4px", fontSize: "0.9rem" }}
                  />
                </div>

                <div>
                  <label htmlFor="comment" style={{ display: "block", fontSize: "0.8rem", fontWeight: 700, marginBottom: "6px" }}>
                    Uw beoordeling (minimaal 10 tekens)
                  </label>
                  <textarea
                    id="comment"
                    required
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    style={{ width: "100%", padding: "10px", border: "1px solid var(--color-gray-300)", borderRadius: "4px", fontSize: "0.9rem", resize: "vertical" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary"
                  style={{ width: "100%", padding: "12px 0", textTransform: "uppercase", fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.08em" }}
                >
                  {submitting ? "Versturen..." : "Beoordeling indienen"}
                </button>
              </form>
            ) : (
              <div style={{ textAlign: "center", padding: "15px 0" }}>
                <p style={{ fontSize: "0.9rem", color: "var(--color-gray-600)", marginBottom: "15px" }}>
                  U moet ingelogd zijn om een beoordeling achter te laten.
                </p>
                <Link href="/account/login" className="btn btn-outline btn-full" style={{ fontSize: "0.85rem", textDecoration: "none" }}>
                  Inloggen
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
